import React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { API_BASE } from "@/api/client";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import StatusBadge from "@/components/StatusBadge";
import { Download, CreditCard } from "lucide-react";
import { openRazorpayCheckout } from "@/lib/razorpay";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

async function downloadPdf(id, code, template = "") {
  const q = template ? `?template=${template}` : "";
  try {
    const res = await fetch(`${API_BASE}/exports/invoice/${id}/pdf${q}`, { credentials: "include" });
    if (!res.ok) {
      const txt = await res.text().catch(() => "");
      toast.error(`Download failed (${res.status})`, { description: txt.slice(0, 120) });
      return;
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${code.replace(/\//g, "_")}-${template || "invoice"}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 4000);
  } catch (e) {
    toast.error("Download failed", { description: e?.message || String(e) });
  }
}

export default function CustomerInvoices() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const { data } = useQuery({ queryKey: ["customer-invoices"], queryFn: () => api.get("/accounts/invoices").then((r) => r.data) });
  const outstanding = (data?.items || []).reduce((s, i) => s + (i.total - (i.paid_amount || 0)), 0);

  const pay = async (inv) => {
    const due = Math.max(0, inv.total - (inv.paid_amount || 0));
    if (due <= 0) return;
    await openRazorpayCheckout({
      amount: due,
      purpose: "order_payment",
      orderId: inv.order_id,
      invoiceId: inv.id,
      description: `Payment for ${inv.code}`,
      prefill: { name: user?.name || "", email: user?.email || "" },
      onSuccess: () => qc.invalidateQueries({ queryKey: ["customer-invoices"] }),
    });
  };

  return (
    <div className="space-y-4" data-testid="customer-invoices-page">
      <div>
        <h1 className="font-display text-3xl font-black tracking-tight">Your invoices</h1>
        <p className="text-sm text-muted-foreground">{data?.total || 0} invoices · ₹{outstanding.toLocaleString("en-IN")} outstanding</p>
      </div>

      <Card>
        <CardContent className="p-0">
          <table className="data-table w-full">
            <thead className="border-b border-border/60 bg-muted/40 text-left text-xs uppercase tracking-widest text-muted-foreground">
              <tr>
                <th className="px-4 py-3">Invoice</th>
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">Order</th>
                <th className="px-4 py-3 text-right">Qty (L)</th>
                <th className="px-4 py-3 text-right">Amount</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {(data?.items || []).map((i) => {
                const due = Math.max(0, i.total - (i.paid_amount || 0));
                return (
                <tr key={i.id} className="border-b border-border/40 text-sm">
                  <td className="px-4 py-3 font-mono font-semibold">{i.code}</td>
                  <td className="px-4 py-3 text-xs">{new Date(i.invoice_date).toLocaleDateString()}</td>
                  <td className="px-4 py-3 font-mono text-xs">{i.order_code}</td>
                  <td className="px-4 py-3 text-right font-mono">{i.quantity_litres}</td>
                  <td className="px-4 py-3 text-right font-mono font-semibold">₹{i.total.toLocaleString("en-IN")}</td>
                  <td className="px-4 py-3"><StatusBadge status={i.payment_status} /></td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1">
                      {due > 0 && (
                        <Button size="sm" onClick={() => pay(i)} data-testid={`pay-inv-${i.code}`}>
                          <CreditCard size={12} className="mr-1" /> Pay ₹{due.toLocaleString("en-IN")}
                        </Button>
                      )}
                      <Button size="sm" variant="outline" onClick={() => downloadPdf(i.id, i.code, "standard")} data-testid={`cust-pdf-std-${i.code}`} title="Standard PDF (corporate)">
                        <Download size={12} className="mr-1" /> Standard
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => downloadPdf(i.id, i.code, "spreadsheet")} data-testid={`cust-pdf-ss-${i.code}`} title="Spreadsheet PDF (tabular grid)">
                        Spreadsheet
                      </Button>
                    </div>
                  </td>
                </tr>
              );})}
              {(!data?.items || data.items.length === 0) && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-sm text-muted-foreground">No invoices yet.</td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
